import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { eur, eurFull } from "@/lib/api";
import { Bar } from "@/components/ScoreGauge";
import { MagnifyingGlass, Sparkle, TrendUp, Target, Handshake, FileText, ArrowRight, Crosshair } from "@phosphor-icons/react";
import { toast } from "sonner";

const KPI_DEFS = [
  { key: "cibles_identifiees", label: "Cibles identifiées", fmt: (v) => v },
  { key: "cibles_contactees", label: "Cibles contactées", fmt: (v) => v },
  { key: "nda_signes", label: "NDA signés", fmt: (v) => v },
  { key: "loi_signees", label: "LOI signées", fmt: (v) => v },
  { key: "dd_en_cours", label: "Due diligences", fmt: (v) => v },
  { key: "acquisitions", label: "Acquisitions", fmt: (v) => v },
  { key: "valeur_acquisitions", label: "Valeur acquisitions", fmt: (v) => eur(v) },
  { key: "ca_cumule", label: "CA cumulé cibles", fmt: (v) => eur(v) },
  { key: "commissions_cumulees", label: "Commissions cumulées", fmt: (v) => eur(v) },
  { key: "roi_previsionnel", label: "ROI prévisionnel", fmt: (v) => `${v}%` },
];

const EXAMPLES = [
  "Cabinet entre 300k€ et 1M€ de commissions, en Île-de-France, orienté entreprise, dirigeant proche retraite",
  "Cabinet santé à Lyon avec moins de 10 salariés",
  "Cible en croissance, faible dépendance dirigeant, multi-compagnies",
];

export default function Dashboard() {
  const navigate = useNavigate();
  const [kpis, setKpis] = useState(null);
  const [query, setQuery] = useState("");
  const [radar, setRadar] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/dashboard/kpis").then((r) => setKpis(r.data)); }, []);

  const runRadar = async () => {
    if (!query.trim()) return;
    setLoading(true); setRadar(null);
    try {
      const { data } = await api.post("/ai/broker-radar", { query });
      setRadar(data);
      if (data.results.length === 0) toast.info("Aucun cabinet ne correspond à ces critères.");
    } catch { toast.error("Erreur Broker Radar"); }
    finally { setLoading(false); }
  };

  return (
    <div>
      {/* Header */}
      <header className="h-16 px-8 flex items-center justify-between border-b border-border bg-white sticky top-0 z-10">
        <div>
          <h1 className="font-head font-bold text-xl">Tableau de bord</h1>
        </div>
        <div className="overline text-muted-foreground">{kpis?.total_companies ?? "—"} cabinets en base</div>
      </header>

      <div className="p-8 space-y-8">
        {/* Broker Radar Hero */}
        <section className="relative overflow-hidden border border-border fade-up"
          style={{ background: "linear-gradient(135deg,#001a5c,#002FA7)" }}>
          <div className="absolute inset-0 opacity-10"
            style={{ backgroundImage: "url(https://images.pexels.com/photos/1029622/pexels-photo-1029622.jpeg)", backgroundSize: "cover" }} />
          <div className="relative p-8 lg:p-10">
            <div className="overline text-white/70 flex items-center gap-2 mb-2"><Sparkle size={14} weight="fill" /> Broker Radar™</div>
            <h2 className="font-head font-black text-2xl lg:text-3xl text-white max-w-2xl mb-2">
              Décrivez le cabinet idéal. L'IA construit votre shortlist.
            </h2>
            <p className="text-white/60 text-sm mb-5 max-w-xl">Shortlist + score + valorisation + probabilité de cession, en une recherche.</p>
            <div className="bg-white/95 backdrop-blur-xl border border-white/20 p-2 flex flex-col sm:flex-row gap-2 max-w-3xl">
              <input data-testid="broker-radar-input" value={query} onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && runRadar()}
                placeholder="Je cherche un cabinet…"
                className="flex-1 px-3 py-2.5 text-sm outline-none bg-transparent" />
              <button data-testid="broker-radar-btn" onClick={runRadar} disabled={loading}
                className="px-5 py-2.5 bg-primary text-white font-semibold text-sm hover:bg-blue-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                <MagnifyingGlass size={16} weight="bold" /> {loading ? "Analyse…" : "Lancer le radar"}
              </button>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {EXAMPLES.map((ex, i) => (
                <button key={ex} onClick={() => setQuery(ex)} data-testid={`radar-example-${i}`}
                  className="text-xs text-white/70 border border-white/20 px-3 py-1.5 hover:bg-white/10 transition-colors text-left">
                  {ex.length > 52 ? ex.slice(0, 52) + "…" : ex}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Radar results */}
        {radar && (
          <section className="fade-up" data-testid="radar-results">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-head font-bold text-lg">Shortlist Broker Radar™ ({radar.results.length})</h3>
              <button onClick={() => setRadar(null)} className="text-xs text-muted-foreground hover:text-foreground">Effacer</button>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {radar.results.map((r) => (
                <div key={r.id} onClick={() => navigate(`/cabinet/${r.id}`)} data-testid={`radar-card-${r.id}`}
                  className="bg-white border border-border p-5 cursor-pointer hover:border-primary transition-colors group">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="font-head font-bold text-base group-hover:text-primary">{r.nom}</div>
                      <div className="text-xs text-muted-foreground">{r.ville} · {r.region}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-2xl font-semibold text-primary leading-none">{r.match}</div>
                      <div className="overline text-muted-foreground">match</div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 my-3">
                    {r.activites.slice(0, 3).map((a) => <span key={a} className="text-[11px] bg-secondary px-2 py-0.5">{a}</span>)}
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center pt-3 border-t border-border">
                    <div><div className="font-mono text-sm font-semibold">{r.scores.transmission}%</div><div className="overline text-muted-foreground">Cession</div></div>
                    <div><div className="font-mono text-sm font-semibold">{r.scores.acquisition}</div><div className="overline text-muted-foreground">Acquis.</div></div>
                    <div><div className="font-mono text-sm font-semibold">{eur(r.valuation.median)}</div><div className="overline text-muted-foreground">Valo.</div></div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* KPIs */}
        <section>
          <h3 className="overline text-muted-foreground mb-3">Indicateurs clés</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 border-t border-l border-border">
            {KPI_DEFS.map((k) => (
              <div key={k.key} data-testid={`kpi-${k.key}`} className="bg-white border-r border-b border-border p-5">
                <div className="overline text-muted-foreground mb-2">{k.label}</div>
                <div className="font-mono text-2xl font-semibold tracking-tight">{kpis ? k.fmt(kpis[k.key]) : "—"}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Cibles prioritaires (taguées par l'analyse 360°) */}
        {kpis && kpis.priority_targets > 0 && (
          <section>
            <button data-testid="priority-targets-card" onClick={() => navigate(`/sourcing?tag=${encodeURIComponent("Cible prioritaire")}`)}
              className="w-full flex items-center gap-4 bg-white border border-teal-300 p-5 hover:bg-teal-50 transition-colors text-left group fade-up">
              <div className="h-12 w-12 rounded-full bg-teal-100 flex items-center justify-center shrink-0">
                <Crosshair size={22} weight="bold" className="text-teal-700" />
              </div>
              <div className="flex-1">
                <div className="font-head font-bold text-lg" data-testid="priority-targets-count">{kpis.priority_targets} cible(s) prioritaire(s)</div>
                <div className="text-sm text-muted-foreground">Cabinets repérés par l'analyse 360° (âge dirigeant élevé, signaux de cession). Cliquez pour les afficher dans le Sourcing.</div>
              </div>
              <ArrowRight size={18} className="text-teal-700 group-hover:translate-x-1 transition-transform" />
            </button>
          </section>
        )}

        {/* Pipeline summary */}
        {kpis && (
          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="overline text-muted-foreground">Répartition du pipeline</h3>
              <button onClick={() => navigate("/pipeline")} className="text-xs text-primary flex items-center gap-1 hover:gap-2 transition-all">
                Voir le pipeline <ArrowRight size={14} />
              </button>
            </div>
            <div className="bg-white border border-border p-6 space-y-3">
              {Object.entries(kpis.pipeline_by_stage).map(([stage, n]) => {
                const max = Math.max(...Object.values(kpis.pipeline_by_stage), 1);
                return (
                  <div key={stage} className="flex items-center gap-4">
                    <div className="w-36 text-sm shrink-0">{stage}</div>
                    <div className="flex-1"><Bar value={(n / max) * 100} /></div>
                    <div className="font-mono text-sm w-6 text-right">{n}</div>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
